/**
 * Watch for forced-logout events. Replaces the legacy `device-watchers.js`
 * with a clean lifecycle: `start()` returns a stop function, and all
 * Firebase listeners are guaranteed to be detached on stop.
 *
 * Two independent watchers:
 *   1. lastActiveDevice — fires when another device claims the active slot.
 *   2. devicePresence  — fires when this device record is removed from
 *      `vehicles/{plate}/devices/{deviceId}` (e.g. admin revocation).
 */

import { ref } from '../../core/firebase.js';
import { getDeviceId, hardLogout } from './device.service.js';
import { normalize as normalizePlate } from '../../shared/plate.js';
import { normalize as normalizePhone } from '../../shared/phone.js';
import { LocalState } from '../../core/localState.js';
import { createLogger } from '../../core/logger.js';
import * as bus from '../../core/eventBus.js';

const log = createLogger('device-watcher');

/** @type {{ stop: () => void } | null} */
let activeRunner = null;

/**
 * Start watchers. Idempotent: calling start() while running stops the previous
 * run first so callers don't accumulate listeners.
 *
 * The `lastActiveDevice` watcher is the single-device-per-phone enforcement —
 * it fires whenever ANOTHER device claims the active slot for this uid, even
 * before the user has registered a vehicle.
 *
 * The `presence` watcher is per-vehicle and only attaches when a plate is
 * provided. Without a plate (phone-only login, vehicle not yet registered)
 * we skip it — there's nothing meaningful to watch at `vehicles/.../devices`
 * until the user owns one.
 *
 * @param {Object} args
 * @param {string} args.uid
 * @param {string} [args.plate]   Optional — phone-only users won't have one yet
 * @returns {() => void}  stop function
 */
export function start({ uid, plate }) {
  if (activeRunner) activeRunner.stop();
  if (!uid) {
    log.warn('start: missing uid — watcher not started');
    return () => {};
  }
  const plateClean = normalizePlate(plate || '');
  const phoneClean = normalizePhone(LocalState.phone || '');
  const deviceId = getDeviceId();

  const lastActiveRef = ref(`users/${uid}/lastActiveDevice/deviceId`);
  // Only build a presence ref when a plate exists — otherwise the presence
  // watcher is a no-op (nothing to listen on yet).
  const presenceRef = plateClean
    ? ref(`vehicles/${plateClean}/devices/${deviceId}`)
    : null;
  // phoneIndex watcher — fires when phoneIndex/{phone} is reassigned to a
  // different uid (cross-device login via SMS triggers `claimPhoneOwnership`
  // which atomically moves the phoneIndex pointer to the new uid AND wipes
  // users/{oldUid}). Without this watcher, the OLD device's lastActiveDevice
  // listener gets a null snapshot (treats it as "no claim", no kick) and
  // the device silently stays "logged in" against an account that no
  // longer exists. The phoneIndex watcher catches that case directly.
  const phoneIndexRef = phoneClean
    ? ref(`phoneIndex/${phoneClean}`)
    : null;

  /** @type {((s: any) => void) | null} */
  let lastActiveCb = null;
  /** @type {((s: any) => void) | null} */
  let presenceCb = null;
  /** @type {((s: any) => void) | null} */
  let phoneIndexCb = null;

  let armed = false;

  // We skip the very first snapshot for each watcher to avoid logging out on
  // the initial state read. The watcher only acts on subsequent changes.
  let lastActiveInitialPassed = false;
  let presenceInitialPassed = false;
  let phoneIndexInitialPassed = false;

  const kickedNotice = {
    message: 'התחברת לחשבון שלך ב-AutoMe ממכשיר אחר. רק מכשיר אחד יכול להיות פעיל בכל רגע, כדי לשמור על האבטחה.',
    button: 'היכנס/י מחדש',
    badge: '🔒 החשבון שלך מוגן',
    iconSvg: '<path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>',
  };

  lastActiveCb = (snap) => {
    const serverDeviceId = snap.val();
    if (!lastActiveInitialPassed) {
      lastActiveInitialPassed = true;
      return;
    }
    // null AFTER initial means the user record (or just lastActiveDevice)
    // was wiped — happens during cross-device transfer when claimPhoneOwnership
    // moves the account to the new uid and deletes users/{oldUid}. Treat as
    // "kicked" — without this branch, the OLD device silently stays "logged in".
    if (!serverDeviceId) {
      log.warn('lastActiveDevice cleared (account moved or deleted) — logging out');
      bus.emit('auth:logged-out', undefined);
      hardLogout('account_moved', kickedNotice);
      return;
    }
    if (serverDeviceId !== deviceId) {
      log.warn('another device became active — logging out');
      bus.emit('auth:logged-out', undefined);
      hardLogout('another_device_active', kickedNotice);
    }
  };

  phoneIndexCb = (snap) => {
    const idxUid = snap.val();
    if (!phoneIndexInitialPassed) {
      phoneIndexInitialPassed = true;
      return;
    }
    // Fires when phoneIndex/{phone} is reassigned (cross-device login) or
    // cleared (account deletion). If it no longer points at us → kick.
    if (idxUid && idxUid !== uid) {
      log.warn('phoneIndex moved to another uid — logging out');
      bus.emit('auth:logged-out', undefined);
      hardLogout('phone_moved', kickedNotice);
    } else if (idxUid === null) {
      log.warn('phoneIndex cleared — account removed elsewhere');
      bus.emit('auth:logged-out', undefined);
      hardLogout('phone_index_cleared', kickedNotice);
    }
  };

  presenceCb = (snap) => {
    if (!presenceInitialPassed) {
      presenceInitialPassed = true;
      return;
    }
    if (!snap.exists()) {
      log.warn('this device was revoked from vehicles/devices — logging out');
      bus.emit('auth:logged-out', undefined);
      hardLogout('device_revoked', {
        message: 'התחברת לחשבון שלך ב-AutoMe ממכשיר אחר. רק מכשיר אחד יכול להיות פעיל בכל רגע, כדי לשמור על האבטחה.',
        button: 'היכנס/י מחדש',
        badge: '🔒 החשבון שלך מוגן',
        iconSvg: '<path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>',
      });
    }
  };

  lastActiveRef.on('value', lastActiveCb, (/** @type {Error} */ err) => {
    log.warn('lastActive listener error:', err.message);
  });
  if (presenceRef) {
    presenceRef.on('value', presenceCb, (/** @type {Error} */ err) => {
      log.warn('presence listener error:', err.message);
    });
  }
  if (phoneIndexRef) {
    phoneIndexRef.on('value', phoneIndexCb, (/** @type {Error} */ err) => {
      log.warn('phoneIndex listener error:', err.message);
    });
  }
  armed = true;
  log.info('started for uid=', uid, 'plate=', plateClean || '(none)', 'phone=', phoneClean || '(none)');

  const stop = () => {
    if (!armed) return;
    armed = false;
    try { lastActiveCb && lastActiveRef.off('value', lastActiveCb); } catch { /* ignore */ }
    try { presenceCb && presenceRef && presenceRef.off('value', presenceCb); } catch { /* ignore */ }
    try { phoneIndexCb && phoneIndexRef && phoneIndexRef.off('value', phoneIndexCb); } catch { /* ignore */ }
    lastActiveCb = null;
    presenceCb = null;
    phoneIndexCb = null;
    log.info('stopped');
    if (activeRunner && activeRunner.stop === stop) activeRunner = null;
  };

  activeRunner = { stop };
  return stop;
}

/** Stop any active watcher. Safe to call when none is running. */
export function stop() {
  if (activeRunner) activeRunner.stop();
}
